import styled, { css } from "styled-components";

import { SmallButton } from "../../pages/Domains/styles";

type Props = {
  /** One emoji, in its little square (as the /domains rows) */
  emoji: string;
  title: string;
  /** A line or two: what shows up here, and how it gets here */
  description?: string;
  /** The one thing to do about it, when there is one */
  action?: { label: string; onClick: () => void };
  /** In a list inside another screen (DNS records, days off…): one quiet row, no button */
  compact?: boolean;
  /** Compact, as a row of a list that already has its frame (DNS records…): no frame of its own */
  inFrame?: boolean;
};

/**
 * What a list says while it has nothing: the emoji in its square (as the /domains rows and the PLUS benefits), a
 * title, what will show up there, and a button when there's something to do about it. Inside another screen
 * (compact): one quiet row.
 */
export const EmptyState = ({ emoji, title, description, action, compact = false, inFrame = false }: Props) => {
  const button = action && (
    <SmallButton type="button" $primary onClick={action.onClick}>
      {action.label}
    </SmallButton>
  );
  const emojiSquare = (
    <span className="emoji" aria-hidden="true">
      {emoji}
    </span>
  );

  if (compact) {
    return (
      <Row $inFrame={inFrame}>
        {emojiSquare}
        <span className="text">
          <strong>{title}</strong>
          {description && <span className="description">{description}</span>}
        </span>
      </Row>
    );
  }

  return (
    <Full>
      {emojiSquare}
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {button}
    </Full>
  );
};

const inter = css`
  &,
  & * {
    font-family: Inter, sans-serif;
  }
`;

/* compact: one quiet row, as the /domains rows */
const Row = styled.div<{ $inFrame: boolean }>`
  ${inter}
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: ${({ $inFrame }) => ($inFrame ? "0.875rem 20px" : "0.75rem 1rem")};
  background: ${({ $inFrame }) => ($inFrame ? "transparent" : "var(--color-neutral-dark)")};
  border-radius: 1rem;

  .emoji {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
    line-height: 1;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 0.625rem;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  strong {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .description {
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }
`;

/* a whole screen with nothing yet: centered, with room around it */
const Full = styled.div`
  ${inter}
  display: flex;
  flex-direction: column;
  align-items: center;
  /* the same room above and below */
  padding: 2.5rem 1.5rem;
  text-align: center;

  .emoji {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    margin-bottom: 1.25rem;
    font-size: 2rem;
    line-height: 1;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 1rem;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.75rem;
    letter-spacing: -0.02em;
    color: var(--color-neutral-white);
  }

  p {
    max-width: 20rem;
    margin-top: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: rgba(255, 255, 255, 0.72);
  }

  button {
    margin-top: 1.5rem;
  }
`;
